'use client';

import { ApartmentReviewsEntry } from '../ApartmentReviews/ApartmentReviewsModule';
import { useTranslations } from 'next-intl';
import { Bath, BedDouble, Building2, ChevronRight, ExternalLink, Ruler } from 'lucide-react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { Link } from '@/src/i18n/navigation';
import { LucideIcon } from '@/src/components/LucideIcon';
import { TrustSignal } from '@/src/components/features/property/TrustSignal';
import { sendEventToApi } from '@/src/components/features/property/lib/analytics';
import {
  emitTrackBuildingPayPerLinkClickEvent,
  emitTrackCostsAndFeesModalOpenEvent,
  emitTrackUpperPpcClickEvent,
} from '@/src/analytics/hotpads/events';
import { TotalMonthlyPriceIndicator } from '@/src/components/features/listings/TotalMonthlyPrice';
import { Anchor, Heading } from '@zillow/constellation-10';
import { cx } from '@/styled-system/css';
import { NeighborhoodMapTrigger } from './sections/NeighborhoodMapTrigger';
import { SpecialOfferBanner } from './sections/SpecialOfferBanner';
import { getSpecialOfferBannerContent } from './sections/specialOfferBanner.utils';
import { getListingSummaryDisplay } from './listingSummaryModule.utils';
import * as styles from './listingSummaryModule.styles';

type Props = {
  listing: PropertyDetailListing;
};

export const ListingSummaryModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const tListing = useTranslations('listing');
  const {
    shouldHideDetails,
    priceDisplay,
    showPrice,
    bedRangeDisplay,
    showBedRange,
    bathRangeDisplay,
    showBathRange,
    sqftDisplay,
    showSqft,
    showUnits,
    otherUnitsCount,
    buildingHref,
    showOtherUnitsLink,
    addressDisplay,
    buildingTitle,
    websiteUrl,
    websiteDisplayName,
    propertyTypeLabel,
    showPropertyType,
  } = getListingSummaryDisplay(listing);

  const hideStreet = Boolean((listing.address as { hideStreet?: boolean } | undefined)?.hideStreet);
  const handleWebsiteClick = () => {
    sendEventToApi('building_website_visited');
    if (listing.maloneLotIdEncoded) {
      sendEventToApi('building_website_clicked', listing.maloneLotIdEncoded);
    }
    emitTrackUpperPpcClickEvent({ listing, triggerLocation: 'home_details_halo' });
    emitTrackBuildingPayPerLinkClickEvent({
      listing,
      triggerLocation: 'home_details_halo',
      triggerObject: 'home_details_component|summary',
    });
  };

  const handleOpenCostsAndFees = () => {
    emitTrackCostsAndFeesModalOpenEvent({ listing });
    if (typeof document === 'undefined') return;
    const target = document.getElementById('cost-and-fees-module');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (shouldHideDetails) {
    return (
      <div className={styles.container}>
        {buildingTitle && (
          <Heading level={1} textStyle="heading-xs" className={styles.buildingNameLegacy}>
            {buildingTitle}
          </Heading>
        )}
      </div>
    );
  }

  const websiteAriaLabel =
    websiteDisplayName && !hideStreet
      ? t('visitWebsiteWithName', { name: websiteDisplayName })
      : t('visitPropertyWebsite');
  const renderCostsLink = () => {
    const linkContent = (
      <>
        <span className={styles.costsLinkText}>{t('costsCalculator')}</span>
        <LucideIcon icon={ChevronRight} size="sm" className={styles.costsLinkChevron} />
      </>
    );

    return (
      <button
        type="button"
        className={styles.costsLink}
        onClick={handleOpenCostsAndFees}
        aria-label={t('viewCostsAndFees')}
        data-testid="link-costs-fees"
      >
        {linkContent}
      </button>
    );
  };

  const showMetaRow = showBedRange || showBathRange || showSqft;
  const metaItems = [
    showBedRange && (
      <span key="beds" className={styles.metaItem} data-testid="text-beds-row">
        <LucideIcon icon={BedDouble} size="sm" />
        {bedRangeDisplay}
      </span>
    ),
    showBathRange && (
      <span key="baths" className={styles.metaItem} data-testid="text-baths-row">
        <LucideIcon icon={Bath} size="sm" />
        {bathRangeDisplay}
      </span>
    ),
    showSqft && (
      <span key="sqft" className={styles.metaItem} data-testid="text-sqft-row">
        <LucideIcon icon={Ruler} size="sm" />
        {sqftDisplay}
      </span>
    ),
  ].filter(Boolean);
  const showIdentityRow = Boolean(buildingTitle || addressDisplay);
  const identityHeadingLabel = [buildingTitle, addressDisplay].filter(Boolean).join(', ');
  const propertyTypeDisplay = propertyTypeLabel ? tListing(propertyTypeLabel.key) : undefined;
  const specialOffer = getSpecialOfferBannerContent(listing, t('specialOffer'));

  const handleSeeFloorPlans = () => {
    if (typeof document === 'undefined') return;
    const target = document.getElementById('units-module');
    if (!target) return;

    const collapsedToggle = target.querySelector<HTMLButtonElement>(
      'button[aria-controls="units-module-details"][aria-expanded="false"]',
    );
    collapsedToggle?.click();

    const scrollToFloorPlans = () => target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (collapsedToggle && typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(scrollToFloorPlans);
      return;
    }

    scrollToFloorPlans();
  };

  return (
    <div className={styles.container}>
      <div className={styles.heroStack} data-testid="cluster-price-decision">
        <TrustSignal listing={listing} />

        <div className={cx(styles.decisionGrid, specialOffer && styles.decisionGridWithOffer)}>
          <div className={styles.leftColumn}>
            {showPrice && (
              <div className={styles.priceGroup}>
                <div className={styles.priceRow}>
                  <span className={styles.priceInfo}>
                    <span className={styles.priceText} data-testid="text-price-range">
                      {`${priceDisplay}${t('perMo')}`}
                    </span>
                    <TotalMonthlyPriceIndicator
                      listing={listing}
                      testId="text-total-price"
                      feesTestId="text-fees-apply"
                    />
                  </span>
                </div>
                {renderCostsLink()}
              </div>
            )}

            <div className={styles.identityWithMap}>
              {showIdentityRow && (
                <Heading
                  level={1}
                  textStyle="heading-xs"
                  className={styles.identityHeading}
                  aria-label={identityHeadingLabel}
                  data-testid="text-identity-line"
                >
                  {(buildingTitle || (websiteUrl && !hideStreet)) && (
                    <span className={styles.identityNameRow}>
                      {buildingTitle && (
                        <span className={styles.identityBuildingName} data-testid="text-content-building-name">
                          {buildingTitle}
                        </span>
                      )}
                      {/* rel includes nofollow: websiteUrl is often a /renter-hub/clickRedirect
                          tracking URL, which robots.txt disallows. A crawlable anchor makes
                          Googlebot queue a URL it is not allowed to fetch -- 23 of a 1,000-URL
                          sample of GSC's "Blocked by robots.txt" list were exactly these
                          (HP-9138). When it is a real landlord site it is still an untrusted
                          outbound link. */}
                      {websiteUrl && !hideStreet && (
                        <Anchor
                          href={websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          aria-label={websiteAriaLabel}
                          className={styles.websiteLink}
                          onClick={handleWebsiteClick}
                          data-testid="link-visit-website"
                        >
                          <LucideIcon icon={ExternalLink} size="sm" />
                        </Anchor>
                      )}
                    </span>
                  )}
                  {addressDisplay && (
                    <span className={styles.identityAddress} data-testid="text-address-inline">
                      {addressDisplay}
                    </span>
                  )}
                </Heading>
              )}
              <NeighborhoodMapTrigger listing={listing} />
            </div>

            {(showMetaRow || showPropertyType || showUnits || showOtherUnitsLink) && (
              <div className={styles.detailsGroup}>
                {showMetaRow && (
                  <div className={styles.metaRow} data-testid="row-unit-meta">
                    {metaItems}
                  </div>
                )}

                {(showPropertyType || showUnits) && (
                  <div className={styles.propertyTypeRow}>
                    {showPropertyType && propertyTypeDisplay && (
                      <span className={styles.metaItem} data-testid="text-property-type">
                        <LucideIcon icon={Building2} size="sm" />
                        {propertyTypeDisplay}
                      </span>
                    )}
                    {showUnits && (
                      <button
                        type="button"
                        className={styles.seeFloorPlansLink}
                        onClick={handleSeeFloorPlans}
                        data-testid="link-see-floor-plans"
                      >
                        {t('seeFloorPlans')}
                      </button>
                    )}
                  </div>
                )}

                {showOtherUnitsLink && (
                  <Link
                    href={buildingHref as Parameters<typeof Link>[0]['href']}
                    className={styles.otherUnitsLink}
                    data-testid="link-other-units-building"
                  >
                    {t('otherRentalUnitsInBuilding', { count: otherUnitsCount })}
                  </Link>
                )}
              </div>
            )}
          </div>

          {specialOffer && <SpecialOfferBanner offer={specialOffer} />}
          <ApartmentReviewsEntry />
        </div>
      </div>
    </div>
  );
};
